import React from 'react'
import {
  Divider,
  Text,
  Button,
  DropdownTheme,
  TextFieldTheme,
  SectionNotification,
} from 'wix-ui-tpa/cssVars'
import {useSettings, useStyles} from '@wix/tpa-settings/react'
import {WidgetProps} from '@wix/yoshi-flow-editor'

import {withReservationsStorageProvider} from '../storage'
import {ReserveButton} from '../components/ReserveButton'
import {SearchAgainButton} from '../components/SearchAgainButton'
import {Form} from '../components/Form'
import {settingsParams} from '../settingsParams'
import {stylesParams} from '../stylesParams'
import {ButtonAlignment, FormLayout, TextAlignment} from '../../../types/createStylesParams'
import {layoutStylesParams} from '../Settings/Tabs/Layout/stylesParams'
import {openBmLocationSettingsPage} from '../../../utils/navigation'
import {RequestStatus} from '../../../utils/wrapRequest'
import {RtlProvider} from '../../../components-shared/RtlProvider'
import {withLayoutSizeProvider} from '../../../components-shared/LayoutSizeProvider'
import {LAYOUT_BREAKPOINTS_START} from '../constants'
import {useDynamicImport} from '../../../utils/useDynamicImport'
import {Breadcrumbs} from '../../../components-shared/Breadcrumbs'
import {ErrorMessage} from '../../../components-shared/ErrorMessage'
import {TimeSlotsList} from '../../../components-shared/TimeSlotsList'
import {TimeSlotsPageType} from '../../../utils/getTimeSlotsPageType'
import {TagsList} from '../../../components-shared/TagsList'
import {TimeSlotsListExtended} from '../../../components-shared/TimeSlotsListExtended'
import {AreasSelector} from '../../../components-shared/AreasSelector'
import {AreasStyle} from '../Settings/Tabs/Layout/settingsParams'

import {useHooks} from './useHooks'
import {classes, st} from './Widget.st.css'
import {reservationsDataHooks} from './constants'
import {getEffectiveFormLayout} from './formLayout'

const Widget: React.FC<WidgetProps> = () => {
  const {
    t,
    submitButtonContentWidth,
    layoutSize,
    containerWidth,
    regionalSettings,
    isInitialDataInProgress,
    getTimeSlotsStatus,
    handleReservationDataStatus,
    isTimeNotAvailableError,
    reservationsPageType,
    partySizeOptions,
    timeOptions,
    partySize,
    selectedDate,
    selectedTime,
    selectedTimeSlot,
    timeSlots,
    areaOptions,
    selectedAreaId,
    selectedAreaName,
    selectedAreaPartySizeErrorKey,
    selectedAreaPartySizeRange,
    handlePartySizeChange,
    handleDateChange,
    handleTimeChange,
    handleTimeSlotChange,
    handleAreaChange,
    filterDate,
    handleSubmit,
    isLargeParty,
    phoneNumber,
    reservationLocationsOptions,
    selectedReservationLocation,
    handleLocationChange,
    shouldShowNoLocationsEditorWarning,
    isPreview,
    metaSiteId,
    showSelectTimeSlotError,
    handleSearchAgainClick,
    isQueryValid,
    handleErrorClick,
    shouldShowSearchAgainButton,
    shouldShowReservationsUnavailable,
    handleHideReservationsUnavailable,
    isNoReservationLocations,
    shouldShowAutoApproveText,
    showNoAvailableTimeSlotNotification,
    breadcrumbsConfig,
  } = useHooks()
  const settings = useSettings()
  const styles = useStyles()

  const ErrorModal = useDynamicImport(() => import('../components/ReserveErrorModal'))

  const isLoading = handleReservationDataStatus === RequestStatus.LOADING
  const getTimeSlotsRequestFailed = getTimeSlotsStatus === RequestStatus.FAILED

  const showTitle = settings.get(settingsParams.showTitle)
  const showDescription = settings.get(settingsParams.showDescription)

  const textAlignment = settings.get(settingsParams.textAlignment)
  const showDivider = settings.get(settingsParams.showDivider)
  const showBreadcrumbs = settings.get(settingsParams.showBreadcrumbs)
  const breadcrumbsAlignment = styles.get(layoutStylesParams.breadcrumbsAlignment)
  const showAreasDescription = settings.get(settingsParams.showAreasDescription)
  const textFieldTheme = settings.get(settingsParams.textFieldTheme)
  const areasStyle = settings.get(settingsParams.areasStyle)
  const dropdownTheme =
    textFieldTheme === TextFieldTheme.Box ? DropdownTheme.Box : DropdownTheme.Line
  const hasAreas = Boolean(areaOptions.length)
  const shouldShowReservationControls = containerWidth !== 0
  const shouldShowAreas = hasAreas && shouldShowReservationControls
  const isAreasDropdown = areasStyle === AreasStyle.Dropdown
  const effectiveFormLayout = getEffectiveFormLayout({
    layoutSize,
    formLayout: settings.get(settingsParams.formLayout),
  })
  const isFormLayoutPacked = effectiveFormLayout === FormLayout.Packed
  const areaDropdownLayoutClass = isFormLayoutPacked ? classes.packed : classes.strip
  const areaDropdownThemeClass = textFieldTheme === TextFieldTheme.Box ? classes.box : classes.line
  const areaDropdownLocationClass =
    reservationLocationsOptions.length > 1 ? classes.withLocation : undefined
  const areaPartySizeErrorText =
    selectedAreaPartySizeErrorKey && selectedAreaPartySizeRange
      ? t(selectedAreaPartySizeErrorKey, {
          ...selectedAreaPartySizeRange,
          areaName: selectedAreaName,
        })
      : undefined
  const unavailableDateText = selectedAreaName
    ? t('uou-reservations.reservations.date-unavailable-area', {
        areaName: selectedAreaName,
        number: partySize,
      })
    : t('uou-reservations.reservations.time-slot-unavailable-date')

  const textAlignmentClass = TextAlignmentToClass[textAlignment]

  const titleText = settings.get(
    shouldShowAutoApproveText ? settingsParams.titleText : settingsParams.titleRequestText,
  )
  const titleTag =
    styles.get(stylesParams.generalTitleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.generalTitleFont).htmlTag

  const secondaryTitleTag =
    styles.get(stylesParams.generalSecondaryTitleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.generalSecondaryTitleFont).htmlTag

  const descriptionText = settings.get(
    shouldShowAutoApproveText
      ? settingsParams.descriptionText
      : settingsParams.descriptionRequestText,
  )

  const subtitleTag =
    styles.get(stylesParams.generalSubtitleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.generalSubtitleFont).htmlTag

  const buttonLabelText = settings.get(
    shouldShowAutoApproveText
      ? settingsParams.buttonLabelText
      : settingsParams.buttonLabelRequestText,
  )

  const buttonAlignment = settings.get(settingsParams.buttonAlignment)
  const buttonAlignmentClass = ButtonAlignmentToClass[buttonAlignment]

  const NotAcceptingBanner = useDynamicImport(() => import('../components/NotAcceptingBanner'))

  if (isNoReservationLocations) {
    return (
      <RtlProvider>
        <div className={st(classes.root, {[layoutSize]: true})}>
          {NotAcceptingBanner ? (
            <NotAcceptingBanner layoutSize={layoutSize} className={classes.notAcceptingBanner} />
          ) : (
            <div />
          )}
        </div>
      </RtlProvider>
    )
  }

  return (
    <RtlProvider>
      <div
        className={st(classes.root, {[layoutSize]: true})}
        data-hook={reservationsDataHooks.root()}
      >
        <div className={classes.contentWrapper}>
          <form className={classes.formRoot} onSubmit={handleSubmit}>
            {shouldShowNoLocationsEditorWarning && (
              <div className={classes.noLocationsEditorWarning}>
                <Text className={st(classes.bodyText)}>
                  {t('uou-reservations.reservations.no-location-editor-title')}
                </Text>
                {isPreview && metaSiteId ? (
                  <Button onClick={() => openBmLocationSettingsPage(metaSiteId)}>
                    {t('uou-reservations.reservations.no-location-editor-cta')}
                  </Button>
                ) : null}
              </div>
            )}
            {(getTimeSlotsRequestFailed || !isQueryValid) && (
              <ErrorMessage
                message={getErrorMessage(t, getTimeSlotsRequestFailed)}
                onClick={handleErrorClick}
              />
            )}

            {showBreadcrumbs && (
              <Breadcrumbs
                items={breadcrumbsConfig}
                className={st(classes.breadcrumbs)}
                alignment={breadcrumbsAlignment}
              />
            )}
            {showTitle || showDescription ? (
              <div className={classes.titleAndSubtitleContainer}>
                {showTitle && (
                  <Text
                    tagName={titleTag}
                    className={st(
                      classes.title,
                      textAlignmentClass,
                      showDescription ? classes.spaceBetweenTitleAndDescription : undefined,
                    )}
                  >
                    {titleText}
                  </Text>
                )}

                {showDescription && (
                  <Text tagName={subtitleTag} className={st(classes.subtitle, textAlignmentClass)}>
                    {descriptionText}
                  </Text>
                )}
              </div>
            ) : null}

            <div>
              <Form
                layoutSize={layoutSize}
                containerWidth={containerWidth}
                regionalSettings={regionalSettings}
                location={{
                  value: selectedReservationLocation?.id ?? undefined,
                  options: reservationLocationsOptions,
                  onChange: handleLocationChange,
                }}
                partySize={{
                  value: partySize.toString(),
                  options: partySizeOptions,
                  onChange: handlePartySizeChange,
                }}
                date={{
                  value: selectedDate,
                  filterDate,
                  onChange: handleDateChange,
                }}
                time={{
                  value: selectedTime?.id,
                  options: timeOptions,
                  onChange: handleTimeChange,
                }}
              />

              {shouldShowAreas ? (
                <>
                  <div className={classes.formAreasDividerContainer}>
                    {showDivider && <Divider className={classes.divider} />}
                  </div>
                  {isAreasDropdown ? (
                    <>
                      <div
                        className={st(
                          classes.areaDropdownRow,
                          areaDropdownLayoutClass,
                          areaDropdownThemeClass,
                          areaDropdownLocationClass,
                          areaPartySizeErrorText ? classes.withAreaPartySizeError : undefined,
                        )}
                      >
                        <AreasSelector
                          label={t('uou-reservations.shared.area')}
                          areaOptions={areaOptions}
                          selectedAreaId={selectedAreaId}
                          showDescription={showAreasDescription}
                          dropdownTheme={dropdownTheme}
                          settingsParams={settingsParams}
                          onChange={handleAreaChange}
                          layoutSize={layoutSize}
                          className={classes.areaDropdown}
                        />
                      </div>
                      {areaPartySizeErrorText ? (
                        <AreaPartySizeNotification message={areaPartySizeErrorText} />
                      ) : null}
                    </>
                  ) : (
                    <>
                      <AreasSelector
                        label={t('uou-reservations.shared.area')}
                        areaOptions={areaOptions}
                        selectedAreaId={selectedAreaId}
                        showDescription={showAreasDescription}
                        dropdownTheme={dropdownTheme}
                        settingsParams={settingsParams}
                        onChange={handleAreaChange}
                        layoutSize={layoutSize}
                        className={st(
                          classes.areasSelector,
                          areaPartySizeErrorText ? classes.withAreaPartySizeError : undefined,
                        )}
                      />
                      {areaPartySizeErrorText ? (
                        <AreaPartySizeNotification message={areaPartySizeErrorText} />
                      ) : null}
                    </>
                  )}
                </>
              ) : null}

              {isLargeParty ? (
                <div className={classes.footer}>
                  <Text className={classes.bodyText}>
                    {t('uou-reservations.reservations.larger-party', {phoneNumber})}
                  </Text>
                </div>
              ) : (
                <>
                  {reservationsPageType === TimeSlotsPageType.DEFAULT ? (
                    <>
                      {!shouldShowAreas ? (
                        <div className={classes.dividerContainer}>
                          {showDivider && <Divider className={classes.divider} />}
                        </div>
                      ) : null}
                      <TimeSlotsList
                        layoutSize={layoutSize}
                        selectedDate={selectedDate}
                        selectedTimeOption={selectedTime}
                        partySize={partySize}
                        selectedAreaName={selectedAreaName}
                        regionalSettings={regionalSettings}
                        items={timeSlots?.[Object.keys(timeSlots)?.[0]]}
                        selectedTimeSlot={selectedTimeSlot}
                        showSelectTimeSlotError={showSelectTimeSlotError}
                        showNoAvailableTimeSlotNotification={showNoAvailableTimeSlotNotification}
                        onSelectTimeSlot={handleTimeSlotChange}
                        renderTagsList={(props) => <TagsList {...props} />}
                        notificationClassName={classes.timeSlotNotificationBodyText}
                      />
                    </>
                  ) : null}

                  {reservationsPageType === TimeSlotsPageType.EXTENDED ? (
                    <TimeSlotsListExtended
                      titleTag={secondaryTitleTag}
                      layoutSize={layoutSize}
                      regionalSettings={regionalSettings}
                      items={timeSlots}
                      partySize={partySize}
                      selectedAreaName={selectedAreaName}
                      selectedDate={selectedDate}
                      selectedTimeOption={selectedTime}
                      selectedTimeSlot={selectedTimeSlot}
                      showSelectTimeSlotError={showSelectTimeSlotError}
                      showNoAvailableTimeSlotNotification={showNoAvailableTimeSlotNotification}
                      onSelectTimeSlot={handleTimeSlotChange}
                      renderTagsList={(tagsListProps) => <TagsList {...tagsListProps} />}
                      notificationClassName={classes.timeSlotNotificationBodyText}
                    />
                  ) : null}

                  <div className={classes.footer}>
                    {reservationsPageType === TimeSlotsPageType.EMPTY &&
                    !isInitialDataInProgress ? (
                      <Text className={classes.bodyText} role="alert">
                        {unavailableDateText}
                      </Text>
                    ) : (
                      <div className={st(classes.reserveButtonWrapper, buttonAlignmentClass)}>
                        <ReserveButton
                          isLoading={isLoading || isInitialDataInProgress}
                          width={submitButtonContentWidth}
                          label={buttonLabelText}
                        />
                      </div>
                    )}

                    {shouldShowSearchAgainButton ? (
                      <div className={st(classes.searchAgainButtonWrapper, buttonAlignmentClass)}>
                        <SearchAgainButton
                          isLoading={isLoading}
                          disabled={isInitialDataInProgress}
                          onClick={handleSearchAgainClick}
                        />
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </form>
          {ErrorModal ? (
            <ErrorModal
              isOpen={shouldShowReservationsUnavailable}
              onClose={handleHideReservationsUnavailable}
              layoutSize={layoutSize}
              errorMessage={getErrorModalMessage(t, isTimeNotAvailableError)}
              errorTitle={getErrorModalTitle(t, isTimeNotAvailableError)}
            />
          ) : (
            <div></div>
          )}
        </div>
      </div>
    </RtlProvider>
  )
}

const TextAlignmentToClass: Record<TextAlignment, string> = {
  [TextAlignment.Left]: classes.left,
  [TextAlignment.Center]: classes.center,
  [TextAlignment.Right]: classes.right,
}

const ButtonAlignmentToClass: Record<ButtonAlignment, string> = {
  [ButtonAlignment.Left]: classes.alignStart,
  [ButtonAlignment.Center]: classes.alignCenter,
  [ButtonAlignment.Right]: classes.alignEnd,
  [ButtonAlignment.Stretch]: classes.alignInitial,
}

const getErrorMessage = (t: (key: string) => string, getTimeSlotsRequestFailed: boolean) => {
  if (getTimeSlotsRequestFailed) {
    return t('uou-reservations.reservations.get-time-slots-error')
  }

  return t('uou-reservations.reservations.refresh-page-error')
}

const AreaPartySizeNotification: React.FC<{message: string}> = ({message}) => (
  <div className={classes.areaPartySizeError} role="alert">
    <SectionNotification className={classes.areaPartySizeNotification}>
      <SectionNotification.Text className={classes.areaPartySizeNotificationText}>
        {message}
      </SectionNotification.Text>
    </SectionNotification>
  </div>
)

const getErrorModalTitle = (t: (key: string) => string, isTimeNotAvailableError: boolean) => {
  if (!isTimeNotAvailableError) {
    return t('uou-reservations.reservations.not-accepting-title')
  }

  return t('uou-reservations.reservations.time-not-available-error-title')
}

const getErrorModalMessage = (t: (key: string) => string, isTimeNotAvailableError: boolean) => {
  if (isTimeNotAvailableError) {
    return t('uou-reservations.reservations.time-not-available-error')
  }

  return t('uou-reservations.reservations.not-accepting-contact-by-phone')
}

export default withLayoutSizeProvider(
  withReservationsStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
)
